import { useCallback, useMemo } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"

interface UseSavePageProps {
  id: string
}

interface UseSavePageValue {
  savePage: (page: number) => void
  savedPage: number | null
}

/**
 * Manages the page in the persistence layer.
 *
 * @example
 * const {
 *   savedPage,
 *   savePage,
 * } = useSavePage({
 *   id: "sitesPerformancePage",
 * })
 */
export const useSavePage = ({ id }: UseSavePageProps): UseSavePageValue => {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => [id], [id])

  const { data } = useQuery({
    gcTime: Infinity,
    queryFn: async (): Promise<number | null> =>
      queryClient.getQueryData(queryKey) ?? null,
    queryKey,
    staleTime: Infinity,
  })

  const savePage = useCallback(
    (page: number) => {
      queryClient.setQueryData(queryKey, page)
      void queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey]
  )

  return {
    savePage,
    savedPage: data ?? null,
  }
}
